// ** React Imports
import { createContext, useEffect, useState } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** Axios
import axios from 'axios'

// ** Config
import authConfig from 'src/configs/auth'
import { replaceWith } from 'src/utils/navigate'
import { buildDeviceUserPayload } from 'src/utils/subscriptionExpire'
import { isUnboundPackageSelectionActive } from 'src/utils/resolveCurrentOrderId'
import {
  isSelectableDevice,
  pickPreferredSelectableDevice
} from 'src/utils/subscriptionStatus'

const bUrl = process.env.NEXT_PUBLIC_BASE_URL
const clientURL = process.env.NEXT_PUBLIC_CLIENT_URL

// ** Defaults
const defaultProvider = {
  user: null,
  loading: true,
  setUser: () => null,
  setLoading: () => Boolean,
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
}
const AuthContext = createContext(defaultProvider)

// request
import { getUserInfo, getAllUserDevice, userDeviceCurrent } from '/src/@api/login'

const getParams = url => {
  try {
    url = url.match(/\?([^#]+)/)[1]
    const obj = {}
    const arr = url.split('&')
    for (let i = 0; i < arr.length; i++) {
      let subArr = arr[i].split('=')
      let key = decodeURIComponent(subArr[0])
      let value = decodeURIComponent(subArr[1])
      obj[key] = value
    }

    return obj
  } catch (err) {
    return null
  }
}

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null || raw === '') return fallback

    return JSON.parse(raw)
  } catch {
    return fallback
  }
}

const AuthProvider = ({ children }) => {
  // ** States
  const [user, setUser] = useState(defaultProvider.user)
  const [loading, setLoading] = useState(defaultProvider.loading)

  // ** Hooks
  const router = useRouter()
  useEffect(() => {
    const initAuth = async () => {
      try {
        const urlParams = getParams(location.search)

        let accessToken = window.localStorage.getItem(authConfig.storageTokenKeyName)

        if (urlParams != null) {
          // 退出登录
          if (urlParams.accessToken == 'clear') {
            window.localStorage.removeItem('userData')
            window.localStorage.removeItem(authConfig.storageTokenKeyName)
            localStorage.removeItem('Basic')
            localStorage.removeItem('Advanced')
            localStorage.removeItem('DeviceUser')
            localStorage.removeItem('currentDevice', JSON.stringify({}))

            replaceWith(`${clientURL}/login?q=clear`)

            return
          }
          if (urlParams.accessToken && urlParams.accessToken != accessToken) {
            localStorage.setItem('Basic', 0)
            localStorage.setItem('Advanced', 0)
            localStorage.setItem('DeviceUser', JSON.stringify({}))
            localStorage.setItem('currentDevice', JSON.stringify({}))
            window.localStorage.setItem(authConfig.storageTokenKeyName, urlParams.accessToken)
          }
        } else if (accessToken == null) {
          if (
            window.location.pathname.substring(1) === 'user-guide-ios' ||
            window.location.pathname.substring(1) === 'user-guide-android' ||
            window.location.pathname.substring(4) === 'user-guide-ios' ||
            window.location.pathname.substring(4) === 'user-guide-android' ||
            window.location.pathname.substring(1) === 'back-up-android' ||
            window.location.pathname.substring(1) === 'back-up-ios' ||
            window.location.pathname.substring(4) === 'back-up-ios' ||
            window.location.pathname.substring(4) === 'back-up-android'
          ) {
            replaceWith(`${clientURL}/login?guide=${encodeURIComponent(`${window.location.origin}${router.asPath}`)}`)

            return
          }
          replaceWith(`${clientURL}/login`)
        }

        let token = window.localStorage.getItem(authConfig.storageTokenKeyName)

        if (!token) return

        let user = await getUserInfo()

        let currentMode = localStorage.getItem('currentMode') || ''

        if (urlParams == null) {
          try {
            let { all, data } = await userDeviceCurrent()

            // 新号常见 { data: null, all: [null] }：有未绑定订单但 platform_type 为空。
            // data != '' 对 null 仍为 true，会把 currentMode 设成 null，随后 indexOf 抛错卡死登录
            // （main 的 14c20bc 修的就是这个；合并时把它并进本分支的重构版本）。
            const modes = (Array.isArray(all) ? all : []).filter(m => m != null && m !== '')
            const mode = data == null || data === '' ? '' : String(data)

            if (mode) {
              if (modes.length >= 2 && localStorage.getItem('currentMode') != null) {
                currentMode = localStorage.getItem('currentMode') || ''
              } else {
                currentMode = mode
                window.localStorage.setItem('currentMode', mode)
              }
            }
          } catch (e) {
            // 当前模式接口失败时沿用本地缓存，不阻断登录态初始化
            console.error('[AuthContext] userDeviceCurrent failed', e?.message || e)
          }
        }

        let requestMode = currentMode
        if (currentMode.indexOf('Android') >= 0) {
          requestMode = 'Android'
        }

        // user-device 偶发 500 时绝不能抛穿：否则 loading 永不结束，AuthGuard 永久转圈（表现为卡死）
        let deviceList = []
        let deviceFetchFailed = false
        try {
          const query = requestMode ? { platform_type: requestMode } : {}
          const allUserDevice = await getAllUserDevice(query)
          deviceList = Array.isArray(allUserDevice?.data) ? allUserDevice.data : []
        } catch (e) {
          deviceFetchFailed = true
          console.error('[AuthContext] getAllUserDevice failed', e?.message || e)
          const cached = readJson('allDeviceUser', [])
          deviceList = Array.isArray(cached) ? cached : []
        }

        if (deviceList.length > 0) {
          // 当前设备：优先按 id 匹配。同一个 iCloud 邮箱可以绑多台设备，
          // 只按 platform_type 取第一个会在刷新时选错账号（侧栏出现两个选中态）。
          const pickId = (...values) => {
            for (const value of values) {
              const id = value == null ? '' : String(value).trim()
              if (id !== '' && !id.includes('@')) return id
            }

            return ''
          }

          const previousDeviceUser = readJson('DeviceUser', null) || {}
          const previousCurrentDevice = readJson('currentDevice', null) || {}
          const preferredId = pickId(
            previousDeviceUser?.device_id,
            previousDeviceUser?.id,
            previousCurrentDevice?.DeviceUser?.device_id,
            previousCurrentDevice?.DeviceUser?.id
          )

          const deviceIdOf = row => (row == null ? '' : String(row.id ?? row.device_id ?? '').trim())

          let getCurrentDev =
            (preferredId
              ? deviceList.find(
                  row =>
                    deviceIdOf(row) === preferredId &&
                    (!currentMode || row?.platform_type === currentMode)
                )
              : null) || deviceList.find(row => row.platform_type == currentMode)

          let currentDevice = readJson('currentDevice', null) || ''

          if (currentMode == 'Android-Local' && currentDevice) {
            let find = deviceList.find(
              row => row.platform_type == currentMode && row.iCloud_username == currentDevice.id
            )

            if (find) {
              getCurrentDev = find
            }
          }

          // 与 SpyX 一致：只恢复仍可选（active/grace）的设备，永不自动选中超 30 天失效设备。
          // 上次用的设备若已过期超 30 天，先回退到同平台其它可选设备；一个都没有则清空选中，
          // 避免出现「侧栏列表里已隐藏、却仍是当前设备」的幽灵选中态。
          if (getCurrentDev && !isSelectableDevice(getCurrentDev)) {
            getCurrentDev = pickPreferredSelectableDevice(deviceList, currentMode) || null

            if (!getCurrentDev) {
              localStorage.setItem('DeviceUser', JSON.stringify({}))
            }
          }

          if (getCurrentDev && !isUnboundPackageSelectionActive()) {
            // 必须写完整字段（含 device_id）：漏掉 device_id 后所有「按 id 找当前设备」
            // 的逻辑只能退回用邮箱反查，同邮箱多账号就会串号。
            // 但侧栏已选中「未绑定的 Package」时不能覆盖：否则刷新后 Package 卡与账号卡
            // 会同时是选中态，功能菜单也会莫名冒出来。
            localStorage.setItem('DeviceUser', JSON.stringify(buildDeviceUserPayload(getCurrentDev)))
          }

          const deviceUser = readJson('DeviceUser', {}) || {}
          if (!deviceUser.username || !deviceUser.password) {
            localStorage.setItem('Basic', 0)
          } else {
            localStorage.setItem('Basic', 1)
          }

          localStorage.setItem('BasicTips', 1)
          localStorage.setItem('Advanced', 1)
          localStorage.setItem('allDeviceUser', JSON.stringify(deviceList))
        } else if (!deviceFetchFailed) {
          // 接口成功但无设备：才清本地标记。接口失败时保留原状态，避免偶发 500 把用户打回未绑定。
          localStorage.setItem('BasicTips', 0)
          localStorage.setItem('Basic', 0)
          localStorage.setItem('Advanced', 0)
          localStorage.setItem('allDeviceUser', JSON.stringify([]))
        }

        user.role = 'admin'
        setUser(user)
        window.localStorage.setItem('userData', JSON.stringify(user))
      } catch (e) {
        console.error('[AuthContext] initAuth failed', e?.message || e)

        // getUserInfo 等关键路径失败时，尽量用本地缓存顶住，避免 AuthGuard 永久转圈
        const cachedUser = readJson('userData', null)
        if (cachedUser && typeof cachedUser === 'object') {
          cachedUser.role = cachedUser.role || 'admin'
          setUser(cachedUser)
        }
      } finally {
        setLoading(false)
      }
    }

    initAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogin = (params, errorCallback) => { }

  const handleLogout = () => {
    setUser(null)
    window.localStorage.removeItem('userData')
    window.localStorage.removeItem(authConfig.storageTokenKeyName)
    localStorage.removeItem('Basic')
    localStorage.removeItem('Advanced')
    localStorage.removeItem('DeviceUser')
    localStorage.removeItem('currentDevice', JSON.stringify({}))

    replaceWith(`${clientURL}/login?q=clear`)
  }

  const values = {
    user,
    loading,
    setUser,
    setLoading,
    login: handleLogin,
    logout: handleLogout
  }

  return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
